import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Grid, TextField, Button, LinearProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNavigate } from 'react-router-dom';
import { getCapacidades } from '../api/capacidadFuncionalApi';

const SORT_OPTIONS = [
  { value: 'fecha_desc', label: 'Más reciente', model: [{ field: 'fecha', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

export default function CapacidadFuncionalTable() {
  const [capacidad, setCapacidad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPaciente, setFilterPaciente] = useState('');
  const [filterProfesional, setFilterProfesional] = useState('');
  const [sortModel, setSortModel] = useState([{ field: 'fecha', sort: 'desc' }]);

  const navigate = useNavigate();

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'fecha_desc';

  const fetchCapacidades = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCapacidades();
      const formatted = data.map(item => {
        const dateObj = new Date(item.createdAt);
        const dd = String(dateObj.getDate()).padStart(2, '0');
        const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
        const yyyy = dateObj.getFullYear();
        const hh = String(dateObj.getHours()).padStart(2, '0');
        const min = String(dateObj.getMinutes()).padStart(2, '0');
        const fecha = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
        return {
          id: item.codigo,
          codigo: item.codigo,
          tipo: 'Capacidad Funcional',
          paciente: `${item.paciente.firstname} ${item.paciente.lastname}`,
          profesional: item.user ? `${item.user.firstname} ${item.user.lastname}` : 'N/A',
          tiempoPiesJuntos: item.tiempoPiesJuntos,
          tiempoTandem: item.tiempoTandem,
          tiempoSemitandem: item.tiempoSemitandem,
          tiempoDesplazamiento: item.tiempoDesplazamiento,
          velocidadMarcha: item.velocidadMarcha,
          sentadillas: item.sentadillas,
          timeUpAndGo: item.timeUpAndGo,
          falloEjecucion: item.falloEjecucion ? 'Sí' : 'No',
          puntosSPPB: item.puntosSPPB,
          pacienteId: item.paciente.id,
          fechaRaw: item.createdAt,
          fecha,
        };
      });
      setCapacidad(formatted);
    } catch (error) {
      console.error('Error fetching capacidades:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCapacidades();
  }, [fetchCapacidades]);

  const filteredRows = capacidad.filter(row =>
    (!filterPaciente || row.paciente.toLowerCase().includes(filterPaciente.toLowerCase())) &&
    (!filterProfesional || row.profesional.toLowerCase().includes(filterProfesional.toLowerCase()))
  );

  const columns = [
    { field: 'pacienteId', headerName: 'ID', width: 30},
    { field: 'paciente', headerName: 'Paciente', width: 180, },
    { field: 'profesional', headerName: 'Profesional', width: 180},
    { field: 'tiempoPiesJuntos', headerName: 'Pies Juntos (s)', width: 140 },
    { field: 'tiempoTandem', headerName: 'Tandem (s)', width: 140 },
    { field: 'tiempoSemitandem', headerName: 'Semitandem (s)', width: 140 },
    { field: 'tiempoDesplazamiento', headerName: 'Desplazamiento (s)', width: 160 },
    { field: 'velocidadMarcha', headerName: 'Velocidad (m/s)', width: 140 },
    { field: 'sentadillas', headerName: 'Sentadillas (s)', width: 140 },
    { field: 'timeUpAndGo', headerName: 'TUG (s)', width: 120 },
    { field: 'falloEjecucion', headerName: 'Fallo Ejecución', width: 120 },
    { field: 'puntosSPPB', headerName: 'Puntos SPPB', width: 120 },
    {
      field: 'fecha', headerName: 'Fecha', width: 180,
      sortComparator: (v1, v2, cp1, cp2) =>
        new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <IconButton
          color="primary"
          onClick={() =>
            navigate(
              `/ver-capacidad-funcional/${params.row.codigo}/${params.row.pacienteId}`
            )
          }
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Typography variant="h6" gutterBottom color="#0D6AEE">
        Informes de Capacidad Funcional
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs>
          <TextField
            name="paciente"
            label="Filtrar por Paciente"
            value={filterPaciente}
            onChange={e => setFilterPaciente(e.target.value)}
            size="small"
            sx={{ mr: 2 }}
          />
          <TextField
            name="profesional"
            label="Filtrar por Profesional"
            value={filterProfesional}
            onChange={e => setFilterProfesional(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={currentSort}
            exclusive
            size="small"
            onChange={(_, val) => {
              const opt = SORT_OPTIONS.find(o => o.value === val);
              if (opt) setSortModel(opt.model);
            }}
          >
            {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              setFilterPaciente('');
              setFilterProfesional('');
              fetchCapacidades();
            }}
          >
            Refrescar
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <LinearProgress />
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            slots={{ toolbar: GridToolbar }}
            disableSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </div>
      )}
    </>
  );
}
